import { ReactElement, useMemo } from 'react'
import {
  createMeshStandardMaterialMemoized,
  getShapeEdges,
  getShapeMatrixTransform,
  getShapeObject,
} from '@scene'
import { ColorRepresentation, DoubleSide } from 'three'
import { useTheme } from '@mui/material'
import { BasicMesh, BasicMeshProps, ImmutableVector3, sceneConfig } from '@modugen/ui-toolkit'
import { MeshStandardMaterialProps } from '@react-three/fiber'
import { useDisposeThreeResourceOnUnmount } from '@editorHooks'

interface Props extends BasicMeshProps {
  points: ImmutableVector3[]
  openings?: ImmutableVector3[][]
  thickness?: number
  translucent?: boolean
  opacity?: number
  /**
   * Can also be set using meshMaterialProps. Used for easier access
   */
  color?: string | ColorRepresentation
  meshMaterialProps?: MeshStandardMaterialProps

  userData?: Record<string, unknown>
  outlines?: boolean
  outlinesColor?: string | ColorRepresentation
  lineWidth?: number
}

const SimpleShape = ({
  points,
  openings = [],
  thickness = 0.01,
  translucent = false,
  opacity,
  color,
  userData = {},
  meshMaterialProps,
  outlines = false,
  outlinesColor,
  lineWidth = 1,
  ...meshProps
}: Props): ReactElement => {
  const { scenePalette } = useTheme()

  const matrixTransform = useMemo(() => getShapeMatrixTransform(points), [points])

  const shapeObject = useMemo(
    () => getShapeObject(points, openings, matrixTransform),
    [points, openings, matrixTransform],
  )

  const material = useMemo(
    () =>
      createMeshStandardMaterialMemoized({
        side: DoubleSide,
        // @ts-ignore
        color: color || scenePalette.elements3d.walls,
        opacity: opacity !== undefined ? opacity : translucent ? 0.33 : 1,
        transparent: translucent || (opacity !== undefined && opacity < 1),
        ...meshMaterialProps,
      }),
    [color, translucent, opacity, meshMaterialProps],
  )

  useDisposeThreeResourceOnUnmount(material)

  const edges = useMemo(() => getShapeEdges(shapeObject, thickness), [shapeObject, thickness])

  useDisposeThreeResourceOnUnmount(edges)

  return (
    <BasicMesh
      // why disabling matrixAutoUpdate should be fine:
      // https://github.com/pmndrs/react-three-fiber/issues/635#issuecomment-682439509
      matrix={matrixTransform}
      matrixAutoUpdate={false}
      userData={{
        ...userData,
        points,
      }}
      material={material}
      {...meshProps}
    >
      <extrudeGeometry
        args={[
          shapeObject,
          {
            depth: thickness,
            bevelEnabled: false,
          },
        ]}
      />

      {outlines && (
        <lineSegments
          layers={sceneConfig.R3FNonSelectableObjectLayer}
          geometry={edges}
          renderOrder={100}
          visible={meshProps.visible}
        >
          <lineBasicMaterial color={outlinesColor || scenePalette.outlines} linewidth={lineWidth} />
        </lineSegments>
      )}
    </BasicMesh>
  )
}

export default SimpleShape
